// SMS module — local-first backing for the SMS Configuration screen
// (activity_sms_configuration.xml + sms_configration_fragment.xml +
// dialog_send_smscategory.xml). The Android app sends through a DLT-registered
// SMS gateway server-side; the web port has no gateway, so this stores the
// gateway/account config, message templates ("groups"), a credit balance and a
// delivery log locally. "Sending" renders each template per recipient and
// records it to the log (decrementing credits) — an honest outbox of what
// would be dispatched, exportable to CSV, with a real `sms:` deep link for
// single sends so a device can hand off to its native SMS app.

import { Customer } from '../types';
import { cachedKey } from './idb';

// ---- gateway / account config (activity_sms_configuration.xml) ----
export interface SmsSettings {
  provider: string;       // gateway name (e.g. MSG91, TextLocal)
  senderId: string;       // 6-char DLT sender / header
  authKey: string;        // gateway API auth key
  shopName: string;
  contactName: string;
  contactMobile: string;
  email: string;
  creditsRemaining: number;
}

// ---- message template / group (sms_configration_fragment.xml ADD GROUP) ----
export interface SmsTemplate {
  id: string;
  name: string;
  body: string;           // up to 160 chars; supports {name}{store}{amount}{bill}{date}
}

// ---- delivery log row (item_sms_delivery.xml) ----
export interface SmsLogEntry {
  id: string;
  toName: string;
  toNumber: string;
  body: string;
  templateName?: string;
  status: 'sent' | 'failed' | 'test';
  timestamp: string;      // ISO
}

const KEYS = {
  settings: 'sconnect.smsSettings',
  templates: 'sconnect.smsTemplates',
  log: 'sconnect.smsLog',
};

export const DEFAULT_SMS_SETTINGS: SmsSettings = {
  provider: '',
  senderId: '',
  authKey: '',
  shopName: '',
  contactName: '',
  contactMobile: '',
  email: '',
  creditsRemaining: 0,
};

// A couple of starter templates so the screen isn't empty on first run.
export const STARTER_TEMPLATES: SmsTemplate[] = [
  { id: 'tpl-thanks', name: 'Bill Thank-You', body: 'Dear {name}, thank you for shopping at {store}. Bill {bill}: Rs {amount}. Visit again!' },
  { id: 'tpl-offer', name: 'Promotional Offer', body: 'Hi {name}, special offer at {store} this week! Show this SMS for a discount. — Team {store}' },
];

const settingsCache = cachedKey<Partial<SmsSettings>>(KEYS.settings, {});
const templatesCache = cachedKey<SmsTemplate[]>(KEYS.templates, STARTER_TEMPLATES);
const logCache = cachedKey<SmsLogEntry[]>(KEYS.log, []);

export function getSmsSettings(): SmsSettings {
  return { ...DEFAULT_SMS_SETTINGS, ...settingsCache.get() };
}
export function saveSmsSettings(s: SmsSettings): void {
  settingsCache.set(s);
}

export function getSmsTemplates(): SmsTemplate[] {
  return templatesCache.get();
}
export function saveSmsTemplates(t: SmsTemplate[]): void {
  templatesCache.set(t);
}

export function getSmsLog(): SmsLogEntry[] {
  return logCache.get();
}
export function saveSmsLog(log: SmsLogEntry[]): void {
  logCache.set(log.slice(-2000));
}

// ---- helpers ----

// GSM SMS segments: 160 chars for a single segment, 153 per part beyond that.
export function smsSegments(len: number): number {
  if (len === 0) return 0;
  return len <= 160 ? 1 : Math.ceil(len / 153);
}

// Replace {placeholders} for a given recipient / store context.
export function renderTemplate(
  body: string,
  ctx: { name?: string; store?: string; amount?: string; bill?: string; date?: string }
): string {
  return body
    .replace(/\{name\}/gi, ctx.name || 'Customer')
    .replace(/\{store\}/gi, ctx.store || '')
    .replace(/\{amount\}/gi, ctx.amount || '')
    .replace(/\{bill\}/gi, ctx.bill || '')
    .replace(/\{date\}/gi, ctx.date || new Date().toLocaleDateString());
}

// Build an `sms:` deep link a device can open in its native messaging app.
export function smsDeepLink(number: string, body: string): string {
  // Android uses ?body=, iOS uses &body=; ?body= is widely accepted.
  return `sms:${number.replace(/[^\d+]/g, '')}?body=${encodeURIComponent(body)}`;
}

// Recipients that actually have a mobile number.
export function smsableCustomers(customers: Customer[]): Customer[] {
  return customers.filter((c) => (c.phone ?? '').replace(/\D/g, '').length >= 7);
}
